import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WebsitePopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/popup-settings`);
      const popupSettings = response.data;
      
      if (!popupSettings.enabled) return;

      // Convert relative image URL to full URL
      if (popupSettings.image_url && !popupSettings.image_url.startsWith('http')) {
        popupSettings.image_url = `${API_URL}${popupSettings.image_url}`;
      }

      // Check if popup should show based on frequency
      if (shouldShowPopup(popupSettings)) {
        setSettings(popupSettings);
        setupTrigger(popupSettings);
      }
    } catch (error) {
      console.error('Error fetching popup settings:', error);
    }
  };

  const shouldShowPopup = (settings) => {
    const currentPath = window.location.pathname;
    
    // Check page restrictions
    if (settings.show_on_pages === 'homepage' && currentPath !== '/') {
      return false;
    }
    
    if (settings.show_on_pages === 'specific') {
      if (!settings.specific_pages.some(page => currentPath.includes(page))) {
        return false;
      }
    }

    // Check frequency
    const lastShown = localStorage.getItem('popup_last_shown');
    const popupClosed = sessionStorage.getItem('popup_closed');

    if (settings.show_frequency === 'once_per_session' && popupClosed === 'true') {
      return false;
    }

    if (settings.show_frequency === 'once_per_day' && lastShown) {
      const lastShownDate = new Date(lastShown);
      const now = new Date();
      const hoursDiff = (now - lastShownDate) / (1000 * 60 * 60);
      if (hoursDiff < 24) {
        return false;
      }
    }

    return true;
  };

  const setupTrigger = (settings) => {
    switch (settings.trigger_type) {
      case 'delay':
        setTimeout(() => {
          showPopup();
        }, settings.delay_seconds * 1000);
        break;

      case 'scroll':
        const handleScroll = () => {
          const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
          if (scrollPercent >= settings.scroll_percentage) {
            showPopup();
            window.removeEventListener('scroll', handleScroll);
          }
        };
        window.addEventListener('scroll', handleScroll);
        break;

      case 'exit_intent':
        const handleMouseLeave = (e) => {
          if (e.clientY <= 0) {
            showPopup();
            document.removeEventListener('mouseleave', handleMouseLeave);
          }
        };
        document.addEventListener('mouseleave', handleMouseLeave);
        break;

      case 'manual':
        // Manual trigger can be implemented via a button or custom event
        break;

      default:
        break;
    }
  };

  const showPopup = () => {
    setIsOpen(true);
    localStorage.setItem('popup_last_shown', new Date().toISOString());
  };

  const closePopup = () => {
    setIsOpen(false);
    sessionStorage.setItem('popup_closed', 'true');
  };

  const handleInputChange = (fieldName, value) => {
    setFormData({ ...formData, [fieldName]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/popup-submission`, formData);
      setIsSuccess(true);
      setTimeout(() => {
        closePopup();
      }, 2000);
    } catch (error) {
      console.error('Error submitting popup form:', error);
      alert('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!settings || !isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: settings.overlay_color }}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl overflow-hidden relative animate-fadeIn"
        style={{ 
          maxWidth: settings.popup_width,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* Close Button */}
        <button
          onClick={closePopup}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
        >
          <X size={24} className="text-gray-700" />
        </button>

        {/* Image Section */}
        {settings.image_url && (
          <div className="w-full h-48 overflow-hidden">
            <img 
              src={settings.image_url} 
              alt="Popup" 
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content Section */}
        <div className="p-6 sm:p-8">
          {isSuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Success!</h3>
              <p className="text-gray-600">{settings.success_message}</p>
            </div>
          ) : (
            <>
              {/* Title & Subtitle */}
              <div className="text-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  {settings.title}
                </h2>
                {settings.subtitle && (
                  <p className="text-gray-600">{settings.subtitle}</p>
                )}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {settings.form_fields.map((field) => (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    
                    {field.type === 'textarea' ? (
                      <textarea
                        name={field.name}
                        placeholder={field.placeholder}
                        required={field.required}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:outline-none"
                        style={{ focusRingColor: settings.primary_color }}
                        rows={3}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        name={field.name}
                        required={field.required}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:outline-none"
                        style={{ focusRingColor: settings.primary_color }}
                      >
                        <option value="">Select {field.label}</option>
                        {field.options.map((option, idx) => (
                          <option key={idx} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        name={field.name}
                        placeholder={field.placeholder}
                        required={field.required}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:outline-none"
                        style={{ focusRingColor: settings.primary_color }}
                      />
                    )}
                  </div>
                ))}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 px-6 rounded-lg text-white font-semibold transition-all hover:shadow-lg disabled:opacity-50"
                  style={{ backgroundColor: settings.primary_color }}
                >
                  {isSubmitting ? 'Submitting...' : settings.submit_button_text}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
